import { App, createVNode, render, getCurrentInstance, Component, markRaw } from 'vue';
import AQModal from '@/components/shared/containers/AQModal.vue';
import { ModalOptions } from '@/interfaces/common';
// Global modal instance
interface ModalInstance {
    close: () => void;
}

let appInstance: App | null = null;
let modalInstance: ModalInstance | null = null;
const modalStack: ModalInstance[] = [];

const ModalService = {
    closeModal() {
        if (modalInstance) {
            modalInstance.close();
        }
    },
    showModal<T>(options: ModalOptions<T>) {
        // Close existing modal if any
        if (modalInstance) {
            modalInstance.close();
        }

        const container = document.createElement('div');
        document.body.appendChild(container);

        let modalVNode: ReturnType<typeof createVNode> | null = null;

        const closeModal = () => {
            const app = appInstance || getCurrentInstance()?.appContext.app;

            if (modalVNode) {
                // Trigger exit animation
                if (modalVNode.component) {
                    const proxy = modalVNode.component.proxy as { visible?: boolean };
                    if (proxy.visible !== undefined) {
                        proxy.visible = false;
                    }
                }
                // Remove from DOM after animation
                setTimeout(() => {
                    if (container.parentNode) {
                        render(null, container);
                        document.body.removeChild(container);
                        modalVNode = null;
                        modalInstance = null;
                        app?.config.globalProperties.$store.dispatch('modalTab/clearTabs');
                    }
                }, 500); // Match this with your modal's transition duration
            }
        };

        // Get the current app instance
        const app = appInstance || getCurrentInstance()?.appContext.app;
        if (!app) {
            const error = 'Could not find Vue app instance';
            console.error(error);
            throw new Error(error);
        }

        // Store
        app.config.globalProperties.$store.dispatch('modalTab/changeActiveTab', { tabIndex: 0 });

        // Create a wrapper component that provides the app context
        const Wrapper: Component = {
            name: 'ModalWrapper',
            setup() {
                const modalProps = {
                    ...options,
                    'component': options.component ? markRaw(options.component) : undefined,
                    'modelValue': true, // Show the modal
                    'onUpdate:modelValue': (val: boolean) => {
                        if (!val) closeModal();
                    },
                    'closeImmediatelyOnConfirm': !!options.closeImmediatelyOnConfirm,
                    'onConfirm': async (data: T) => {
                        if (options.onConfirm) {
                            await options.onConfirm(data);
                        }
                        const { closeImmediatelyOnConfirm = true } = options || {};
                        if (closeImmediatelyOnConfirm) {
                            closeModal();
                        }
                    },
                    'onSecondButton': async (data: T) => {
                        if (options.onConfirmSecondButton) {
                            await options.onConfirmSecondButton(data);
                        }
                        closeModal();
                    },
                    'onClose': () => {
                        if (options.onClose) {
                            options.onClose();
                        }
                        closeModal();
                    },
                    // Pass through custom event listeners
                    ...(options.listeners || {}),
                };

                return () => {
                    const vnode = createVNode(AQModal, modalProps);
                    return vnode;
                };
            },
        };

        try {
            // Create a vnode for the modal with the wrapper
            modalVNode = createVNode(Wrapper);

            // Ensure the vnode has app context
            modalVNode.appContext ??= app._context;
        } catch (error) {
            console.error('Error creating VNode:', error);
            throw error;
        }

        try {
            // Render the modal
            render(modalVNode, container);

            // Store the close function
            modalInstance = { close: closeModal };
            return modalInstance;
        } catch (error) {
            console.error('Error rendering modal:', error);
            // Clean up if rendering fails
            if (container.parentNode) {
                document.body.removeChild(container);
            }
            throw error;
        }
    },

    addModal<T>(options: ModalOptions<T>) {
        // If no modal is currently visible, just use showModal
        if (!modalInstance && modalStack.length === 0) {
            return this.showModal(options);
        }

        // Keep existing modal(s) open and stack a new one on top
        const container = document.createElement('div');
        document.body.appendChild(container);

        let modalVNode: ReturnType<typeof createVNode> | null = null;

        const closeModal = () => {
            if (modalVNode) {
                // Trigger exit animation
                if (modalVNode.component) {
                    const proxy = modalVNode.component.proxy as { visible?: boolean };
                    if (proxy.visible !== undefined) {
                        proxy.visible = false;
                    }
                }
                // Remove from DOM after animation
                setTimeout(() => {
                    if (container.parentNode) {
                        render(null, container);
                        document.body.removeChild(container);
                        modalVNode = null;
                        // Remove this modal from the stack
                        const index = modalStack.findIndex((m) => m.close === closeModal);
                        if (index !== -1) {
                            modalStack.splice(index, 1);
                        }
                    }
                }, 300); // Match this with your modal's transition duration
            }
        };

        // Get the current app instance
        const app = appInstance || getCurrentInstance()?.appContext.app;
        if (!app) {
            const error = 'Could not find Vue app instance';
            throw new Error(error);
        }

        // Store
        app.config.globalProperties.$store.dispatch('modalTab/changeActiveTab', { tabIndex: 0 });

        // Create a wrapper component that provides the app context
        const Wrapper: Component = {
            name: 'ModalWrapper',
            setup() {
                const modalProps = {
                    ...options,
                    'component': options.component ? markRaw(options.component) : undefined,
                    'modelValue': true, // Show the modal
                    'onUpdate:modelValue': (val: boolean) => {
                        if (!val) closeModal();
                    },
                    'closeImmediatelyOnConfirm': !!options.closeImmediatelyOnConfirm,
                    'onConfirm': async (data: T) => {
                        if (options.onConfirm) {
                            await options.onConfirm(data);
                        }
                        const { closeImmediatelyOnConfirm = true } = options || {};
                        if (closeImmediatelyOnConfirm) {
                            closeModal();
                        }
                    },
                    'onSecondButton': async (data: T) => {
                        if (options.onConfirmSecondButton) {
                            await options.onConfirmSecondButton(data);
                        }
                        closeModal();
                    },
                    'onClose': () => {
                        if (options.onClose) {
                            options.onClose();
                        }
                        closeModal();
                    },
                    // Pass through custom event listeners
                    ...(options.listeners || {}),
                };

                return () => {
                    const vnode = createVNode(AQModal, modalProps);
                    return vnode;
                };
            },
        };

        // eslint-disable-next-line no-useless-catch
        try {
            // Create a vnode for the modal with the wrapper
            modalVNode = createVNode(Wrapper);

            // Ensure the vnode has app context
            modalVNode.appContext ??= app._context;
        } catch (error) {
            throw error;
        }

        try {
            // Render the modal
            render(modalVNode, container);

            // Add this modal to the stack
            const newModalInstance = { close: closeModal };
            modalStack.push(newModalInstance);
            return newModalInstance;
        } catch (error) {
            // Clean up if rendering fails
            if (container.parentNode) {
                document.body.removeChild(container);
            }
            throw error;
        }
    },
};

export function setupModalService(app: App) {
    // Store the app instance
    appInstance = app;
    // Make it available globally
    app.config.globalProperties.$modal = ModalService;
    // For Composition API
    app.provide('modal', ModalService);
}
export type ModalServiceType = typeof ModalService;

export default ModalService;
